import React, { useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { forceCollide } from 'd3-force';
import { Box } from '@mui/material';
import { useThemeTokens } from '../../hooks/useThemeTokens';

// Generic force-directed network graph extracted from the Expert IQ affinity /
// attribute graphs (which were copy-paste siblings). All the layout machinery —
// container width measurement, session position cache (so incremental node
// appends render as organic growth instead of a reshuffle), root pinning at
// (0,0), charge/link/collide forces, drag-to-pin — lives here verbatim; the
// domain-specific bits (colors, radii, labels) come in as props.

// Pending-edit styling shared by the Expert IQ editors: nodes/links staged as
// added/removed get green/red accents. Data without pendingState is unaffected.
const PENDING_COLORS = {
  added: '#16A34A',
  removed: '#DC2626',
};

export type PendingState = 'added' | 'removed' | undefined;

export interface ForceNetworkGraphNodeBase {
  id: string;
  name: string;
  // UI-only: flags a node staged for add/remove before Save.
  pendingState?: PendingState;
}

export interface ForceNetworkGraphLink {
  source: string;
  target: string;
  pendingState?: PendingState;
}

export interface ForceNetworkGraphLabel {
  text: string;
  bold?: boolean;
  // Optional small grey second line under the main label (e.g. the parent
  // attribute group of an attribute option).
  caption?: string;
}

interface ForceNetworkGraphProps<N extends ForceNetworkGraphNodeBase> {
  nodes: N[];
  links: ForceNetworkGraphLink[];
  nodeColor: (node: N) => string;
  nodeRadius: (node: N) => number;
  // Return null to skip the label entirely (e.g. dense leaf nodes). Defaults
  // to the node name, regular weight.
  nodeLabel?: (node: N) => ForceNetworkGraphLabel | null;
  onNodeClick?: (node: N) => void;
  rootId?: string;
  height?: number;
}

interface CachedPos {
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx?: number;
  fy?: number;
}

// Measures the container width so the canvas fills its column (ForceGraph2D
// otherwise defaults to the full window size and overflows).
function useContainerWidth() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

export function ForceNetworkGraph<N extends ForceNetworkGraphNodeBase>({
  nodes,
  links,
  nodeColor,
  nodeRadius,
  nodeLabel,
  onNodeClick,
  rootId,
  height = 520,
}: ForceNetworkGraphProps<N>) {
  const { ref, width } = useContainerWidth();
  // Canvas 2D can't read CSS custom properties — resolved hex per theme.
  const { tokens: themeTokens } = useThemeTokens();
  const fgRef = useRef<any>(null);
  // Remembers node positions/pins across re-clones (e.g. staging an edit or an
  // incremental append) so the layout doesn't reshuffle and dropped nodes stay
  // put — session-only, not persisted.
  const positions = useRef<Map<string, CachedPos>>(new Map());

  // Collision radius in graph units: node circle + room for the centered label
  // so labels of neighbouring nodes don't overlap. Scales with label length.
  const collideRadius = (node: any): number => {
    const r = nodeRadius(node as N);
    const labelLen = (node.name || '').length;
    return Math.min(120, r + 10 + labelLen * 3);
  };

  // Reset all cached positions/pins whenever the focus entity (root) changes —
  // switching modes or drilling into a node must lay out fresh, otherwise a node
  // that was a neighbour (or was dragged/pinned) keeps its stale spot when it
  // becomes the new root.
  const prevRootId = useRef<string | undefined>(undefined);
  if (prevRootId.current !== rootId) {
    positions.current.clear();
    prevRootId.current = rootId;
  }

  // react-force-graph mutates node/link objects in place (x/y/vx/vy and link
  // source/target → node refs). Clone on every change so our React state stays
  // pristine, seeding any remembered position so the graph stays stable.
  const graphData = useMemo(
    () => ({
      nodes: nodes.map((n) => {
        const c = positions.current.get(n.id);
        if (c) return { ...n, x: c.x, y: c.y, vx: c.vx, vy: c.vy, fx: c.fx, fy: c.fy };
        // Anchor the root at the layout centre so it stays isolated and
        // everything else radiates out around it.
        if (n.id === rootId) return { ...n, x: 0, y: 0, fx: 0, fy: 0 };
        return { ...n };
      }),
      links: links.map((l) => ({ ...l })),
    }),
    [nodes, links, rootId],
  );

  // Spread nodes out for readable labels: stronger repulsion, longer links, and
  // a collision force sized to the label so neighbours don't overlap.
  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    const charge = fg.d3Force('charge');
    // The root (hub) repels much harder so its many spokes can't crowd back
    // over it — otherwise a popular hub overlaps its leaf nodes.
    if (charge) charge.strength((node: any) => (node.id === rootId ? -2000 : -450));
    const link = fg.d3Force('link');
    if (link) link.distance(150).strength(0.3);
    // Strict collision (high strength + iterations) so many leaf nodes in
    // dense views can't overlap.
    fg.d3Force('collide', forceCollide().radius(collideRadius).strength(1).iterations(6));
    if (fg.d3VelocityDecay) fg.d3VelocityDecay(0.3);
    if (fg.d3ReheatSimulation) fg.d3ReheatSimulation();
    // `width` is a dep on purpose: ForceGraph2D only mounts once width > 0
    // (see the render below), so on first paint fgRef is still null and this
    // effect early-returns. Re-running when width flips 0 → positive is what
    // actually applies these forces — without it the graph keeps d3's weak
    // defaults (no collision) and nodes overlap / links sit too short.
  }, [graphData, rootId, width]);

  const rememberPositions = () => {
    graphData.nodes.forEach((n: any) => {
      if (typeof n.x !== 'number') return;
      positions.current.set(n.id, { x: n.x, y: n.y, vx: n.vx ?? 0, vy: n.vy ?? 0, fx: n.fx, fy: n.fy });
    });
  };

  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        height,
        border: '1px solid var(--border)',
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'var(--surface)',
      }}
    >
      {width > 0 && (
        <ForceGraph2D
          ref={fgRef}
          graphData={graphData}
          width={width}
          height={height}
          cooldownTicks={300}
          nodeRelSize={1}
          onEngineStop={rememberPositions}
          // Pin a node where it's dropped — no snapback (session only).
          onNodeDragEnd={(node: any) => {
            node.fx = node.x;
            node.fy = node.y;
            positions.current.set(node.id, {
              x: node.x,
              y: node.y,
              vx: 0,
              vy: 0,
              fx: node.x,
              fy: node.y,
            });
          }}
          linkColor={(link: any) => {
            if (link.pendingState === 'added') return PENDING_COLORS.added;
            if (link.pendingState === 'removed') return PENDING_COLORS.removed;
            return '#CBB89A';
          }}
          linkWidth={(link: any) => (link.pendingState ? 2 : 1)}
          linkLineDash={(link: any) => (link.pendingState === 'removed' ? [4, 3] : null)}
          onNodeClick={(node: any) => onNodeClick && onNodeClick(node as N)}
          nodeCanvasObject={(node: any, ctx, globalScale) => {
            const n = node as N & { x: number; y: number };
            const r = nodeRadius(n);
            const removed = n.pendingState === 'removed';
            const added = n.pendingState === 'added';

            ctx.globalAlpha = removed ? 0.45 : 1;
            ctx.beginPath();
            ctx.arc(n.x, n.y, r, 0, 2 * Math.PI);
            ctx.fillStyle = nodeColor(n);
            ctx.fill();

            if (added || removed) {
              ctx.lineWidth = 2 / globalScale;
              ctx.strokeStyle = added ? PENDING_COLORS.added : PENDING_COLORS.removed;
              ctx.stroke();
            }

            const label = nodeLabel ? nodeLabel(n) : { text: n.name || '' };
            if (label) {
              const fontSize = Math.max(10 / globalScale, 2.5);
              const labelY = n.y + r + 1.5;
              ctx.font = `${label.bold ? 'bold ' : ''}${fontSize}px Sans-Serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'top';
              ctx.fillStyle = themeTokens.text;
              ctx.fillText(label.text || '', n.x, labelY);
              if (label.caption) {
                const captionFont = Math.max(8 / globalScale, 2);
                ctx.font = `${captionFont}px Sans-Serif`;
                ctx.fillStyle = themeTokens.text2;
                ctx.fillText(label.caption, n.x, labelY + fontSize + 1);
              }
            }
            ctx.globalAlpha = 1;
          }}
          nodePointerAreaPaint={(node: any, color, ctx) => {
            const n = node as N & { x: number; y: number };
            const r = nodeRadius(n) + 3;
            ctx.beginPath();
            ctx.arc(n.x, n.y, r, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
          }}
        />
      )}
    </Box>
  );
}
